import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { daysUntil, hasMoved, relativeDayLabel } from '@/lib/betaConsole';
import { LoadingNote, ErrorNote } from '@/admin';

/** The protocol timeline. Only `date` is editable — labels and notes are seeded
 *  server-side so the reasoning behind each milestone cannot drift per-console.
 *  Moving a gate date re-derives the preflight banner's day counts. */
export function TimelineTab({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['beta-timeline'], queryFn: () => adminApi.beta.timeline.list() });

  const patch = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string }) => adminApi.beta.timeline.patch(id, date),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['beta-timeline'] }); },
  });

  if (q.isLoading) return <LoadingNote />;
  if (q.isError) {
    return (
      <ErrorNote>
        Could not load the timeline. The <code>/admin/beta/timeline</code> endpoint is owned by
        beta-console-backend and may not be deployed here yet.
      </ErrorNote>
    );
  }

  const dates = q.data ?? [];

  return (
    <>
      <section className="mb-13 rounded border border-border bg-surface p-13">
        <h2 className="mb-2 text-body font-semibold text-text-primary">Working backwards</h2>
        <div className="rounded border-l-2 border-accent bg-surface-sunken p-13">
          <p className="mb-2 text-body-sm text-text-primary">
            <strong>The start-of-August date is the build freeze, not onboarding.</strong> A deadline
            asked for to stop over-polishing is a <em>build</em> deadline. Onboarding is bound by
            something else entirely — recruiting twelve people, consenting them, getting them to a
            collection centre fasted, and waiting on the assay. That is three weeks of human
            scheduling that no amount of shipping compresses.
          </p>
          <p className="text-body-sm text-text-primary">
            This still beats a mid-September onboarding by three weeks, and lands synthesis in
            mid-December rather than January — clear of the summer dead zone that would otherwise
            have gutted the third check-in.
          </p>
        </div>
        <p className="mt-2 text-body-sm text-text-muted">
          Dates are editable. Marked gates are dates that should not move without a reason you would
          write down.
        </p>
      </section>

      <section className="mb-13 rounded border border-border bg-surface p-13">
        <ol className="ml-0 list-none">
          {dates.map((d) => {
            const dd = daysUntil(d.date);
            return (
              <li key={d.id} className="border-b border-border py-2 last:border-b-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-semibold text-text-primary">{d.label}</span>
                  {d.gate ? (
                    <span className="rounded-pill px-2 py-0.5 text-[11px] font-semibold mod-json">gate</span>
                  ) : null}
                  {canEdit ? (
                    <input
                      type="date"
                      className="searchbox !mb-0 !w-auto font-mono text-[11px]"
                      aria-label={`Date — ${d.label}`}
                      value={d.date}
                      onChange={(e) => patch.mutate({ id: d.id, date: e.target.value })}
                    />
                  ) : (
                    <span className="font-mono text-[11px] text-text-secondary">{d.date}</span>
                  )}
                  <span className="font-mono text-[11px] text-text-muted">{relativeDayLabel(dd)}</span>
                  {hasMoved(d) ? (
                    // A moved gate date should be visibly moved. Silently
                    // re-baselining is how a slipped date stops looking slipped.
                    <span className="font-mono text-[10px] text-flagged" title={`Seeded date ${d.default_date}`}>
                      moved from {d.default_date}
                    </span>
                  ) : null}
                </div>
                {d.note ? <p className="mt-1 text-body-sm text-text-secondary">{d.note}</p> : null}
              </li>
            );
          })}
        </ol>
      </section>
    </>
  );
}
